/* JCI product listing: progressive enhancement over Shopify's native ProductFacet. */
(() => {
  const rootSelector = '[data-jci-product-listing]';
  const desktopQuery = window.matchMedia('(min-width: 750px)');
  const tabletQuery = window.matchMedia('(min-width: 750px) and (max-width: 999px)');
  const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  const storageKey = 'jci-product-listing:grid:v1';
  const mobileStorageKey = 'jci-product-listing:mobile-grid:v1';
  let activeVideo = null;
  const mediaTransitions = new WeakMap();
  const mediaRequests = new WeakMap();

  const videoObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting && !entry.target.paused) entry.target.pause();
        });
      }, { rootMargin: '80px' })
    : null;

  const readPreference = (key, fallback, allowed) => {
    try {
      const value = window.sessionStorage.getItem(key);
      return allowed.includes(value) ? value : fallback;
    } catch (error) {
      return fallback;
    }
  };

  const writePreference = (key, value) => {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      // Storage may be disabled. The control still works for this page view.
    }
  };

  const hydrateFrame = (frame) => {
    if (!frame) return Promise.resolve();

    const image = frame.querySelector('[data-jci-media-image]');
    if (image && frame.dataset.jciHydrated !== 'true') {
      /* JCI EDITS - hydration only ever runs for the frame the shopper is
         switching TO, so that image is wanted right now: drop it out of
         lazy loading before assigning the source. Without this the browser
         is free to defer the fetch of an image it doesn't consider near
         the viewport, and img.decode() below then never settles - which
         stalls the await in activateMedia and leaves the card stuck on its
         current frame. Reproducible on a card that is only partially
         scrolled into view when a swipe or segment tap lands on it. */
      image.loading = 'eager';
      if (image.dataset.srcset) image.srcset = image.dataset.srcset;
      if (image.dataset.src) image.src = image.dataset.src;
      image.removeAttribute('data-srcset');
      image.removeAttribute('data-src');
    }

    frame.dataset.jciHydrated = 'true';

    if (!image || (image.complete && image.naturalWidth > 0)) return Promise.resolve();

    /* JCI EDITS - activateMedia awaits this, so it MUST always settle.
       image.decode() alone does not guarantee that: for an image the
       browser has not decided to paint (a card well below the fold, which
       a swipe or a programmatic switch can still target) Chrome leaves the
       promise pending indefinitely, and the card then never changes frame.
       Settle on whichever comes first - decode, load, error, or a short
       cap. The cap only means the frame swaps in a beat before the decode
       finishes, which is what an ordinary <img> does anyway. */
    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        resolve();
      };
      const timer = window.setTimeout(finish, 600);

      image.addEventListener('load', finish, { once: true });
      image.addEventListener('error', finish, { once: true });
      if (typeof image.decode === 'function') image.decode().then(finish, finish);
    });
  };

  const pauseVideo = (video) => {
    if (!video) return;
    video.pause();
    video.closest('[data-jci-media-frame]')?.classList.remove('is-video-playing');
    if (activeVideo === video) activeVideo = null;
  };

  const activateVideo = (frame) => {
    const video = frame?.querySelector('[data-jci-media-video]');
    if (!video) {
      if (activeVideo) pauseVideo(activeVideo);
      return;
    }

    if (activeVideo && activeVideo !== video) pauseVideo(activeVideo);
    if (!video.src && video.dataset.src) {
      video.src = video.dataset.src;
      video.removeAttribute('data-src');
      video.load();
      videoObserver?.observe(video);
    }

    frame.classList.add('is-video-playing');
    activeVideo = video;
    const playPromise = video.play();
    if (playPromise) playPromise.catch(() => frame.classList.remove('is-video-playing'));
  };

  const settleMediaTransition = (mediaRoot) => {
    const state = mediaTransitions.get(mediaRoot);
    if (state?.timer) window.clearTimeout(state.timer);
    if (state?.frame) window.cancelAnimationFrame(state.frame);

    const currentIndex = Number(mediaRoot.dataset.jciActiveIndex || 0);
    mediaRoot.querySelectorAll('[data-jci-media-frame]').forEach((frame, index) => {
      const isCurrent = index === currentIndex;
      frame.classList.toggle('is-current', isCurrent);
      frame.classList.remove('is-entering', 'is-entering-from-left', 'is-entering-from-right', 'is-revealed');
      frame.hidden = !isCurrent;
    });
    mediaTransitions.set(mediaRoot, { timer: 0, frame: 0 });
  };

  const activateMedia = async (mediaRoot, nextIndex, options = {}) => {
    if (!mediaRoot) return;
    const frames = Array.from(mediaRoot.querySelectorAll('[data-jci-media-frame]'));
    if (!frames.length) return;

    const boundedIndex = Math.max(0, Math.min(Number(nextIndex) || 0, frames.length - 1));
    const requestId = (mediaRequests.get(mediaRoot) || 0) + 1;
    mediaRequests.set(mediaRoot, requestId);

    await hydrateFrame(frames[boundedIndex]);
    if (mediaRequests.get(mediaRoot) !== requestId) return;

    const currentIndex = Number(mediaRoot.dataset.jciActiveIndex || 0);
    if (boundedIndex === currentIndex) {
      mediaRoot.dataset.jciReady = 'true';
      if (options.playVideo !== false) activateVideo(frames[boundedIndex]);
      return;
    }

    const currentFrame = frames[currentIndex];
    const nextFrame = frames[boundedIndex];
    const revealDirection = boundedIndex > currentIndex ? 'right' : 'left';
    settleMediaTransition(mediaRoot);
    nextFrame.hidden = false;
    nextFrame.classList.add('is-entering', `is-entering-from-${revealDirection}`);

    if (currentFrame && currentFrame !== nextFrame) {
      pauseVideo(currentFrame.querySelector('[data-jci-media-video]'));
    }

    mediaRoot.dataset.jciActiveIndex = String(boundedIndex);
    mediaRoot.dataset.jciReady = 'true';
    mediaRoot.querySelectorAll('[data-jci-media-select]').forEach((button) => {
      const selected = Number(button.dataset.jciMediaSelect) === boundedIndex;
      button.classList.toggle('is-active', selected);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    const revealFrame = window.requestAnimationFrame(() => {
      nextFrame.classList.add('is-revealed');
      if (options.playVideo !== false) activateVideo(nextFrame);
      const timer = window.setTimeout(() => settleMediaTransition(mediaRoot), 380);
      mediaTransitions.set(mediaRoot, { timer, frame: 0 });
    });
    mediaTransitions.set(mediaRoot, { timer: 0, frame: revealFrame });
  };

  const resetMedia = (mediaRoot) => activateMedia(mediaRoot, 0, { playVideo: false });

  /* JCI EDITS - NOTE ON THE `is-filter-open` STATE BELOW.
     The desktop filter sidebar (.product-facet__aside / #facet-filters)
     is rendered on every JCI listing page but is never expanded: the
     shipped design exposes filtering through the inline Make/Model/Type
     dropdowns in the toolbar instead, so main-collection.liquid no longer
     emits the [data-jci-filter-toggle] button that would flip this state.
     The aside itself must stay in the DOM regardless - it IS the native
     ProductFacet form that every AJAX refresh here is dispatched at (see
     facet:criteria-changed below and in jci-mobile-filters.js).
     The open/close plumbing is left in place, unused and inert, so the
     sidebar variant can be re-enabled by restoring that one button rather
     than rebuilding the behaviour. Deliberate; not an oversight. */
  const effectiveGrid = (root) => {
    // JCI EDITS - list view isn't squeezed by the filter sidebar the way the
    // 4-column grid is (it scrolls horizontally instead), so it's exempt
    // from the existing "collapse to 3 columns while filters are open" rule.
    if (root.dataset.jciGrid === 'list') return 'list';
    return root.classList.contains('is-filter-open') && desktopQuery.matches
      ? '3'
      : root.dataset.jciGrid || '4';
  };

  /* JCI EDITS - Model values ("3", "1500", "Silverado") carry no Make of
     their own from Search & Discovery, so they're unreadable in a flat
     list. Label each one from the static window.JCIModelMakeMap (see
     jci-model-make-map.js). Shared by the desktop Model dropdown
     (jci-product-query-control.liquid) and the mobile filter drawer
     (jci-mobile-filters.liquid) - both mark their rows with
     data-jci-mf-model/data-jci-mf-make-label the same way. Runs from
     syncControls so it re-applies after every AJAX facet refresh. */
  const decorateModelLabels = (root) => {
    const map = window.JCIModelMakeMap;
    if (!map) return;
    root.querySelectorAll('[data-jci-mf-model]').forEach((row) => {
      // Most values map to exactly one make, but some genuinely span more
      // than one (badge-engineered twins, shared truck platforms, or a
      // handful of tagging inconsistencies already present in the live
      // catalog) - see jci-model-make-map.js for specifics. Join whatever
      // is actually on record rather than picking one and hiding the rest.
      const makes = map[row.dataset.jciMfModel];
      const text = makes && makes.length ? makes.join(', ') : null;
      const label = row.querySelector('[data-jci-mf-make-label]');
      if (text && label && label.textContent !== text) {
        label.textContent = text;
        label.hidden = false;
      }
    });
  };

  /* JCI EDITS - the live "N Products" count next to the page title
     (collection-banner.liquid's .jci-page-stats, [data-jci-live-count])
     lives in a different section than the toolbar, so ProductFacet's AJAX
     refresh (which only swaps #facet-main) never touches it directly. The
     up-to-date number is always sitting right here though, in the
     visually-hidden tally this same refresh DOES re-render (see
     data-jci-query-tally in main-collection.liquid) - just copy it across
     after every refresh, same as decorateModelLabels does for Model rows. */
  const syncLiveCount = (root) => {
    const tally = root.querySelector('[data-jci-query-tally] strong');
    const target = document.querySelector('[data-jci-live-count]');
    if (tally && target && target.textContent !== tally.textContent) {
      target.textContent = tally.textContent;
    }
  };

  /* JCI EDITS - collection and YMM palettes are independent global settings.
     The body follows the collection palette; the sibling YMM app section
     follows data-jci-ymm-theme. Re-run after Theme Editor section reloads and
     AJAX facet refreshes so neither setting is lost. */
  const syncListingTheme = (root) => {
    const isLight = root.dataset.jciListingTheme === 'light';
    document.body.classList.toggle('jci-product-listing-theme--light', isLight);
    document.body.classList.toggle('jci-product-listing-theme--dark', !isLight);

    const ymmSection = document.querySelector('.jci-collection-list-page__ymm');
    if (ymmSection) {
      const ymmTheme = root.dataset.jciYmmTheme || document.body.dataset.jciYmmTheme || 'dark';
      const isYmmLight = ymmTheme === 'light';
      ymmSection.classList.toggle('jci-collection-list-page__ymm--light', isYmmLight);
      ymmSection.classList.toggle('jci-collection-list-page__ymm--dark', !isYmmLight);
    }
  };

  const syncControls = (root) => {
    syncListingTheme(root);
    decorateModelLabels(root);
    syncLiveCount(root);
    if (window.JCIMobileFilters) window.JCIMobileFilters.boot();
    const effective = effectiveGrid(root);
    root.querySelectorAll('[data-jci-grid-choice]').forEach((button) => {
      button.setAttribute('aria-pressed', button.dataset.jciGridChoice === effective ? 'true' : 'false');
      button.disabled = root.classList.contains('is-filter-open') && button.dataset.jciGridChoice === '4';
    });
    root.querySelectorAll('[data-jci-mobile-grid-choice]').forEach((button) => {
      button.setAttribute('aria-pressed', button.dataset.jciMobileGridChoice === root.dataset.jciMobileGrid ? 'true' : 'false');
    });

    const toggle = root.querySelector('[data-jci-filter-toggle]');
    const isOpen = root.classList.contains('is-filter-open');
    if (toggle) {
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      const label = toggle.querySelector('[data-jci-filter-label]');
      if (label) label.textContent = isOpen ? 'Hide filters' : 'Show filters';
    }

    const aside = root.querySelector('.product-facet__aside');
    if (aside) {
      if (desktopQuery.matches) {
        aside.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      } else {
        // The theme reuses this facet markup inside its mobile drawer. Do not
        // leave a desktop-only hidden state behind after a viewport change.
        aside.removeAttribute('aria-hidden');
      }
    }
  };

  const setFilterOpen = (root, open) => {
    root.classList.toggle('is-filter-open', open);
    root.classList.toggle('is-filter-closed', !open);
    syncControls(root);
  };

  /* JCI EDITS - Make/Model/Type search fields. Clears a control's search
     box (and un-hides anything it had filtered out) whenever that control
     closes, so it doesn't reopen still filtered from last time. */
  const resetQuerySearch = (control) => {
    const search = control?.querySelector('[data-jci-query-search]');
    if (!search || !search.value) return;
    search.value = '';
    control.querySelectorAll('.jci-product-query-option[hidden]').forEach((option) => {
      option.hidden = false;
    });
  };

  const initializeRoot = (root) => {
    if (!root || root.dataset.jciProductListingInitialized === 'true') {
      if (root) syncControls(root);
      return;
    }

    root.dataset.jciProductListingInitialized = 'true';
    root.dataset.jciGrid = tabletQuery.matches
      ? '3'
      : readPreference(storageKey, root.dataset.jciGrid || '4', ['3', '4', 'list']);
    root.dataset.jciMobileGrid = readPreference(mobileStorageKey, '2', ['1', '2']);
    setFilterOpen(root, false);

    root.addEventListener('click', (event) => {
      const queryFilterLink = event.target.closest('[data-jci-query-filter-url]');
      if (queryFilterLink && root.contains(queryFilterLink)) {
        if (queryFilterLink.getAttribute('aria-disabled') === 'true') {
          event.preventDefault();
          return;
        }

        const facetFilters = root.querySelector('#facet-filters');
        if (facetFilters) {
          event.preventDefault();
          const control = queryFilterLink.closest('[data-jci-query-control]');
          control?.removeAttribute('open');
          resetQuerySearch(control);
          facetFilters.dispatchEvent(new CustomEvent('facet:criteria-changed', {
            bubbles: true,
            detail: { url: queryFilterLink.href }
          }));
        }
        return;
      }

      const queryTrigger = event.target.closest('[data-jci-query-control] > summary');
      if (queryTrigger && root.contains(queryTrigger)) {
        const control = queryTrigger.parentElement;
        if (control?.classList.contains('is-disabled')) {
          event.preventDefault();
          return;
        }

        root.querySelectorAll('[data-jci-query-control][open]').forEach((openControl) => {
          if (openControl !== control) {
            openControl.removeAttribute('open');
            resetQuerySearch(openControl);
          }
        });
      } else if (!event.target.closest('[data-jci-query-control]')) {
        root.querySelectorAll('[data-jci-query-control][open]').forEach((control) => {
          control.removeAttribute('open');
          resetQuerySearch(control);
        });
      }

      const filterToggle = event.target.closest('[data-jci-filter-toggle]');
      if (filterToggle && root.contains(filterToggle)) {
        event.preventDefault();
        setFilterOpen(root, !root.classList.contains('is-filter-open'));
        return;
      }

      const gridChoice = event.target.closest('[data-jci-grid-choice]');
      if (gridChoice && root.contains(gridChoice) && !gridChoice.disabled) {
        root.dataset.jciGrid = gridChoice.dataset.jciGridChoice;
        writePreference(storageKey, root.dataset.jciGrid);
        syncControls(root);
        return;
      }

      const mobileGridChoice = event.target.closest('[data-jci-mobile-grid-choice]');
      if (mobileGridChoice && root.contains(mobileGridChoice)) {
        root.dataset.jciMobileGrid = mobileGridChoice.dataset.jciMobileGridChoice;
        writePreference(mobileStorageKey, root.dataset.jciMobileGrid);
        syncControls(root);
        return;
      }

      const mediaChoice = event.target.closest('[data-jci-media-select]');
      if (mediaChoice && root.contains(mediaChoice)) {
        event.preventDefault();
        event.stopPropagation();
        const mediaRoot = mediaChoice.closest('[data-jci-product-media]');
        activateMedia(mediaRoot, mediaChoice.dataset.jciMediaSelect);
        return;
      }

      const mediaLink = event.target.closest('[data-jci-media-link]');
      if (mediaLink && Number(mediaLink.dataset.jciSuppressClickUntil || 0) > Date.now()) {
        event.preventDefault();
      }
    });

    root.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const openControl = root.querySelector('[data-jci-query-control][open]');
      if (!openControl) return;
      openControl.removeAttribute('open');
      resetQuerySearch(openControl);
      openControl.querySelector('summary')?.focus();
    });

    /* JCI EDITS - Make/Model/Type search fields: filter the option list
       client-side against the label text already in the DOM (no extra
       request, no separate data source). */
    root.addEventListener('input', (event) => {
      const search = event.target.closest('[data-jci-query-search]');
      if (!search || !root.contains(search)) return;
      const query = search.value.trim().toLowerCase();
      search.closest('[data-jci-query-control]')?.querySelectorAll('.jci-product-query-option').forEach((option) => {
        option.hidden = query.length > 0 && !(option.dataset.jciQueryOptionName || '').includes(query);
      });
    });

    let pendingPointerFrame = 0;
    root.addEventListener('pointermove', (event) => {
      if (!hoverQuery.matches || pendingPointerFrame) return;
      const mediaLink = event.target.closest('[data-jci-media-link]');
      if (!mediaLink || !root.contains(mediaLink)) return;
      const card = mediaLink.closest('.jci-product-card');
      if (card?.dataset.jciVariantSelected === 'true') return;

      pendingPointerFrame = window.requestAnimationFrame(() => {
        pendingPointerFrame = 0;
        const mediaRoot = mediaLink.closest('[data-jci-product-media]');
        const count = Number(mediaRoot?.dataset.jciMediaCount || 0);
        if (count < 2) return;
        const rect = mediaLink.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(0.999, (event.clientX - rect.left) / rect.width));
        activateMedia(mediaRoot, Math.floor(ratio * count));
      });
    });

    root.addEventListener('pointerout', (event) => {
      if (!hoverQuery.matches) return;
      const mediaRoot = event.target.closest('[data-jci-product-media]');
      if (!mediaRoot || mediaRoot.contains(event.relatedTarget)) return;
      const currentIndex = Number(mediaRoot.dataset.jciActiveIndex || 0);
      const currentFrame = mediaRoot.querySelectorAll('[data-jci-media-frame]')[currentIndex];
      pauseVideo(currentFrame?.querySelector('[data-jci-media-video]'));
    });

    /* JCI EDITS - touch-swipe media stepping, ported from /collections'
       burst cards (initBurst's handleTouchStart/handleTouchEnd in
       jci-collection-list.js) - same 34px minimum travel, same 1.15
       horizontal-vs-vertical ratio (so a vertical page scroll started
       inside a card doesn't get misread as a swipe), same 450ms post-
       swipe click suppression on the link so the swipe doesn't also
       navigate to the product. Wraps at either end like activateMedia's
       caller already expects (activateMedia itself only clamps). */
    root.addEventListener('touchstart', (event) => {
      if (event.touches.length !== 1) return;
      const mediaLink = event.target.closest('[data-jci-media-link]');
      if (!mediaLink || !root.contains(mediaLink)) return;
      mediaLink.dataset.jciTouchStartX = String(event.touches[0].clientX);
      mediaLink.dataset.jciTouchStartY = String(event.touches[0].clientY);
    }, { passive: true });

    root.addEventListener('touchend', (event) => {
      const mediaLink = event.target.closest('[data-jci-media-link]');
      if (!mediaLink || !root.contains(mediaLink)) return;
      const startX = Number(mediaLink.dataset.jciTouchStartX);
      const startY = Number(mediaLink.dataset.jciTouchStartY);
      delete mediaLink.dataset.jciTouchStartX;
      delete mediaLink.dataset.jciTouchStartY;
      if (!Number.isFinite(startX) || !Number.isFinite(startY) || !event.changedTouches.length) return;

      const deltaX = event.changedTouches[0].clientX - startX;
      const deltaY = event.changedTouches[0].clientY - startY;
      if (Math.abs(deltaX) < 34 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) return;

      event.preventDefault();
      const card = mediaLink.closest('.jci-product-card');
      if (card?.dataset.jciVariantSelected === 'true') return;
      const mediaRoot = mediaLink.closest('[data-jci-product-media]');
      const count = Number(mediaRoot?.dataset.jciMediaCount || 0);
      if (count < 2) return;
      const current = Number(mediaRoot?.dataset.jciActiveIndex || 0);
      activateMedia(mediaRoot, (current + (deltaX < 0 ? 1 : -1) + count) % count);
      mediaLink.dataset.jciSuppressClickUntil = String(Date.now() + 450);
    }, { passive: false });

    root.addEventListener('change', (event) => {
      if (!event.target.matches('.product-item-meta__swatch-list .color-swatch__radio')) return;
      const card = event.target.closest('.jci-product-card');
      if (card) card.dataset.jciVariantSelected = 'true';
      const mediaRoot = card?.querySelector('[data-jci-product-media]');
      if (mediaRoot) resetMedia(mediaRoot);
    });

    const main = root.querySelector('#facet-main');
    if (main && 'MutationObserver' in window) {
      const observer = new MutationObserver(() => syncControls(root));
      observer.observe(main, { childList: true, subtree: false });
    }

    syncControls(root);
  };

  const initializeAll = (scope = document) => scope.querySelectorAll(rootSelector).forEach(initializeRoot);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));

  const handleViewportChange = () => {
    initializeAll();
    document.querySelectorAll(rootSelector).forEach((root) => {
      if (tabletQuery.matches) {
        root.dataset.jciGrid = '3';
      } else if (desktopQuery.matches) {
        root.dataset.jciGrid = readPreference(storageKey, root.dataset.jciGrid || '4', ['3', '4', 'list']);
      }
      syncControls(root);
    });
  };

  [desktopQuery, tabletQuery].forEach((query) => {
    if (query.addEventListener) {
      query.addEventListener('change', handleViewportChange);
    } else {
      query.addListener(handleViewportChange);
    }
  });

  /* JCI EDITS - hide the live-chat launcher while the mobile Filters
     drawer, Sort drawer, or header nav drawer is open (all three cover
     sticky bottom content/CTAs otherwise). Reuses the exact mechanism
     CartDrawer already uses for this (theme.js's attributeChangedCallback
     toggles body.jci-cart-drawer-open; theme.css's chat-widget selector
     list hides the launcher on it) - see the :is() there - rather than a
     second hiding method. #jci-mobile-filters (the actual mobile Filters
     drawer - facet-filters.liquid's native #facet-filters is what
     desktop's sidebar toggle opens instead, watched too in case that ever
     runs as a drawer), #sort-by-popover, and #mobile-menu-drawer (the
     header hamburger menu, mobile-menu.liquid) are all native
     OpenableElements (DrawerContent/PopoverContent in theme.js - the nav
     drawer's MobileNavigation extends DrawerContent directly), which
     already reflect their state as a real `open` attribute, so a small
     MutationObserver is enough; no need to hook their open/close
     callbacks directly. This script is scoped to the listing pages
     (layout/theme.liquid), so the nav-drawer case below only applies
     while the shopper is on one of them - which is exactly where a
     covered sticky CTA was reported. Mobile-only per desktopQuery, since Sort's popover opens on
     desktop too (as a dropdown, not a drawer) and shouldn't hide the
     launcher there. */
  const overlaySelector = '#jci-mobile-filters[open], #facet-filters[open], #sort-by-popover[open], #mobile-menu-drawer[open]';
  let overlaySyncQueued = false;
  const syncChatLauncherVisibility = () => {
    overlaySyncQueued = false;
    const anyDrawerOpen = Boolean(document.querySelector(overlaySelector));
    document.body.classList.toggle('jci-facet-drawer-open', anyDrawerOpen && !desktopQuery.matches);
  };
  const queueOverlaySync = () => {
    if (overlaySyncQueued) return;
    overlaySyncQueued = true;
    requestAnimationFrame(syncChatLauncherVisibility);
  };

  /* ProductFacet replaces #facet-main after every AJAX filter request,
     including the mobile drawer inside it. Observe the document rather
     than keeping references to the first set of drawer nodes, otherwise
     chat hiding stops after the first "Show results" refresh. */
  new MutationObserver(queueOverlaySync).observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['open'],
  });

  if (desktopQuery.addEventListener) {
    desktopQuery.addEventListener('change', queueOverlaySync);
  } else {
    desktopQuery.addListener(queueOverlaySync);
  }

  syncChatLauncherVisibility();
})();
